import { CircularProgress, FormControl, FormHelperText, InputLabel } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';

import Autocomplete from '../EveeAutocomplete';
import EveeTextField from '../EveeTextField';

import styles from './styles';

const AsyncAutocomplete = ({
  value,
  displayField,
  renderOption,
  renderInput,
  defaultOptions,
  options: staticOptions,
  label,
  disabled,
  noOptionsText,
  TextFieldProps,
  InputLabelProps,
  captionText,
  error,
  required,
  helperText,
  requestMethod,
  parseResult,
  limit,
  classes,
  filterOptions: customFilterOptions,
  onChange,
  ...rest
}) => {
  const [focus, setFocus] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const hasSearchText = Boolean(inputValue?.trim());
  const hasStaticOptions = Array.isArray(staticOptions);

  const displayOptions = useMemo(() => {
    if (hasStaticOptions) {
      return staticOptions;
    }

    return hasSearchText ? options : defaultOptions;
  }, [defaultOptions, hasSearchText, hasStaticOptions, options, staticOptions]);

  const filterOptions = requestMethod ? (x) => x : customFilterOptions;

  const renderDefaultInputJsx = (params) => (
    <EveeTextField
      {...params}
      fullWidth
      error={error}
      variant="outlined"
      InputProps={{
        ...params.InputProps,
        className: classes.input,
        endAdornment: (
          <>
            {loading && <CircularProgress color="inherit" size={20} />}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
      {...TextFieldProps}
    />
  );

  const retrieveOptions = async (inputValue) => {
    const searchString = inputValue?.trim();

    if (!searchString || !requestMethod) {
      setLoading(false);
      setOptions([]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const result = parseResult(await requestMethod(searchString));

      if (currentRequestId === requestIdRef.current) {
        setOptions(result ? result.slice(0, limit) : []);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const onInputChange = async (_event, newInputValue) => {
    const valueString = newInputValue ?? '';

    setInputValue(valueString);
    await retrieveOptions(valueString);
  };

  return (
    <FormControl
      required={required}
      error={error}
      focused={focus}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {label && (
        <InputLabel
          variant="standard"
          shrink={false}
          style={{ marginBottom: '4px' }}
          {...InputLabelProps}
        >
          {label}
        </InputLabel>
      )}

      <Autocomplete
        autoComplete
        includeInputInList
        filterSelectedOptions
        disableClearable
        value={value}
        disabled={disabled}
        forcePopupIcon={false}
        options={displayOptions}
        renderOption={renderOption}
        noOptionsText={hasSearchText ? noOptionsText : captionText}
        getOptionSelected={(option, val) => option[displayField] === val[displayField]}
        getOptionLabel={(option) => option[displayField] || ''}
        // Keep MUI client-side filtering disabled only for server-side search.
        filterOptions={filterOptions}
        renderInput={renderInput || renderDefaultInputJsx}
        classes={{
          popper: classes.popper,
          paper: classes.paper,
          option: classes.option,
          listbox: classes.listbox,
        }}
        onInputChange={onInputChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onChange={(_event, newValue) => onChange(newValue)}
        {...rest}
      />

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

AsyncAutocomplete.propTypes = {
  displayField: PropTypes.string.isRequired,
  value: PropTypes.shape({}),
  noOptionsText: PropTypes.string,
  defaultOptions: PropTypes.arrayOf(PropTypes.shape({})),
  captionText: PropTypes.string,
  requestMethod: PropTypes.func,
  parseResult: PropTypes.func,
  renderOption: PropTypes.func,
  renderInput: PropTypes.func,
  limit: PropTypes.number,
  label: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  required: PropTypes.bool,
  TextFieldProps: PropTypes.shape({
    placeholder: PropTypes.string,
}),
  InputLabelProps: PropTypes.shape({}),
  classes: PropTypes.shape({
    input: PropTypes.string,
    paper: PropTypes.string,
    option: PropTypes.string,
    popper: PropTypes.string,
    listbox: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.func,
  onChange: PropTypes.func,
};

AsyncAutocomplete.defaultProps = {
  noOptionsText: 'No options',
  captionText: 'Start typing to see options',
  value: {},
  defaultOptions: [],
  requestMethod: null,
  parseResult: (r) => r,
  renderOption: null,
  renderInput: null,
  label: null,
  limit: 5,
  disabled: false,
  error: false,
  required: false,
  helperText: '',
  TextFieldProps: {
    placeholder: '',
  },
  InputLabelProps: {},
  onChange: () => {},
};

export default withStyles(styles)(AsyncAutocomplete);
